import React, { useState, useEffect } from 'react'
import { shallow } from 'zustand/shallow'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAccount, useNetwork } from 'wagmi'

import { EthNftMint } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsEthNft/EthNftMint'
import { BscNftMint } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models'
import { MaticNftMint } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsMaticNft/MaticNftMint'
import { EthGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsEthSmartContract/getStatus'
import { MaticGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsMaticSmartContract/maticCheckStatus'
import { BscGetTxStatus } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/modelsBscSmartContract/BscGetTxStatus'

import useBlockchainAdapter from '../../hooks/useBlockchainAdapter'
import { useAuthState } from '../../contexts/authContext'
import { useChainAndNetwork } from 'src/store/chainAndNetwork'

import ConnectEvmInfo from '../../components/ConnectEvmInfo'
import SectionTitle from '../../components/kit/SectionTitle'
import BoxInputField from '../../components/kit/BoxInputField'
import FormActionButton from '../../components/kit/FormActionButton'
import { networksProviders } from '../../util/networksRpcs'
import { evmNetworkIds, evmNetworkIdsType } from '../../util/chainIds'
import { mintNft, getTxStatus } from '../../contexts/blockchainActionCreators'

type ethChainIdsType = {
  testnet: 11155111
  mainnet: 1
}

const EvmForm = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const uri = queryParams.get('tokenUri')
  const scAddress = queryParams.get('scAddress')

  const [address, setAddress] = useState<string>('')
  const [disabled, setDisabled] = useState<boolean>(false)
  const [tokenUri, setTokenUri] = useState<string>(uri ?? '')
  const [smartContractAddress, setSmartContractAddress] = useState<string>(scAddress ?? '')
  let url = new URL(window.location.href)

  const adapter = useBlockchainAdapter()
  const walletConnectAccount = useAccount()
  const { chain } = useNetwork()

  const isWalletConnected =
    walletConnectAccount?.isConnected ||
    (adapter.metamask?.connector?.isConnected() &&
      adapter.metamask?.connector?._state?.isUnlocked &&
      adapter.metamask?.connector?.selectedAddress)

  const { chain: selectedChain, network } = useChainAndNetwork(
    (state) => ({
      chain: state.chain,
      network: state.network,
    }),
    shallow,
  )

  const user = useAuthState().user
  const navigate = useNavigate()

  const walletConnectChainIdMismatch =
    walletConnectAccount?.isConnected &&
    chain?.id !== evmNetworkIds[selectedChain.toUpperCase() as keyof evmNetworkIdsType]?.[network]

  const metamaskChainIdMismatch =
    adapter.metamask?.connector?.isConnected() &&
    adapter.metamask?.connector?.selectedAddress &&
    adapter.metamask?.connector?._state?.isUnlocked &&
    parseInt(adapter.metamask?.connector?.networkVersion) !==
      evmNetworkIds[selectedChain.toUpperCase() as keyof evmNetworkIdsType]?.testnet

  const ethChains: ethChainIdsType = {
    mainnet: 1,
    testnet: 11155111,
  }

  const handleTokenUriChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('tokenUri', e.target.value)
    window.history.pushState({}, '', url)
    setTokenUri(e.target.value)
  }

  const handleScAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('scAddress', e.target.value)
    window.history.pushState({}, '', url)
    setSmartContractAddress(e.target.value)
  }

  const checkFieldRules = (string: string): boolean => {
    const regex = /^\S+(?:\s+\S+)*$/
    return regex.test(string)
  }

  const checkContractAddressRules = (): boolean => {
    const regex = /^0x[a-fA-F0-9]{40}$/
    return regex.test(smartContractAddress)
  }

  const mintPayload: EthNftMint | BscNftMint | MaticNftMint = {
    address,
    currentAddress: address,
    networkProvider:
      networksProviders[selectedChain.toUpperCase() as keyof evmNetworkIdsType][network],
    environment: network,
    tokenUri,
    smartContractAddress,
    ...(selectedChain === 'ethereum' ? { chainId: ethChains[network] } : {}),
  }

  const requestAdapter = walletConnectAccount?.isConnected
    ? adapter.walletConnect
    : adapter.metamask?.connector?.isConnected() && adapter.metamask?.connector?._state?.isUnlocked
    ? adapter.metamask
    : null

  useEffect(() => {
    if (walletConnectAccount?.isConnected && walletConnectAccount?.address && !address) {
      setAddress(walletConnectAccount?.address)
    }
  }, [walletConnectAccount?.isConnected])

  useEffect(() => {
    if (
      adapter.metamask?.connector?.isConnected() &&
      adapter.metamask?.connector?.selectedAddress &&
      adapter.metamask?.connector?._state?.isUnlocked &&
      !address
    ) {
      setAddress(adapter?.metamask?.connector?.selectedAddress)
    }
  }, [
    adapter.metamask?.connector?.isConnected(),
    adapter.metamask?.connector?.selectedAddress,
    adapter.metamask?.connector?._state?.isUnlocked,
  ])

  let txCheckIntervalId: any = null
  const verifyProject = async (txHash: string) => {
    const getTxStatusPayload: EthGetTxStatus | BscGetTxStatus | MaticGetTxStatus = {
      address,
      networkProvider:
        networksProviders[selectedChain.toUpperCase() as keyof evmNetworkIdsType][network],
      txhash: txHash,
      environment: network,
      ...(selectedChain === 'ethereum' ? { currentAddress: address } : {}),
      ...(selectedChain === 'ethereum' ? { chainId: ethChains[network] } : {}),
    }

    try {
      const txStatus = await getTxStatus(
        getTxStatusPayload,
        selectedChain.toUpperCase() as keyof evmNetworkIdsType,
        user?.accessToken,
      )
      if (txStatus?.data?.data?.status) {
        clearInterval(txCheckIntervalId)
        navigate('/projects')
        setDisabled(false)
      }
    } catch (error) {
      console.error('error', error)
      setDisabled(false)
      clearInterval(txCheckIntervalId)
      if (error instanceof Error) {
        toast.error(error?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  const handleMintNft = async () => {
    setDisabled(true)

    try {
      const response = await mintNft(
        mintPayload,
        selectedChain.toUpperCase() as keyof evmNetworkIdsType,
        user?.accessToken,
      )

      const {
        data,
        from,
        to,
      }: {
        data: string
        from: string
        to: string
      } = response?.data?.data || {}

      const params = {
        from,
        to,
        data,
      }

      if (metamaskChainIdMismatch) {
        await adapter.metamask?.switchNetwork(selectedChain, network)
      }

      const txResponse = await requestAdapter.sendTransaction(params)

      txCheckIntervalId = setInterval(() => {
        verifyProject(txResponse)
      }, 10000)
    } catch (error) {
      console.error('error', error)
      setDisabled(false)
      if (error instanceof Error) {
        toast.error('Something went wrong. Please try again.', {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return (
    <>
      <ConnectEvmInfo
        infoText="You have to connect your wallet before minting a NFT."
        isAddressSet={Boolean(address)}
        setAddress={setAddress}
      />
      <SectionTitle title="Settings" />
      <BoxInputField
        title="URI link"
        alignTitleCenter
        placeholder='e.g. "https://my-token-uri.com"'
        value={tokenUri}
        onChange={handleTokenUriChange}
        error={!checkFieldRules(tokenUri) && tokenUri.length > 0}
      />
      <BoxInputField
        title="Smart contract address"
        alignTitleCenter
        placeholder='e.g. "0x23..."'
        value={smartContractAddress}
        onChange={handleScAddressChange}
        error={!checkContractAddressRules() && smartContractAddress.length > 0}
      />
      <FormActionButton
        text="Mint NFT"
        onClick={handleMintNft}
        disabledState={disabled}
        disabled={
          disabled ||
          !address ||
          !checkFieldRules(tokenUri) ||
          !checkContractAddressRules() ||
          !isWalletConnected ||
          walletConnectChainIdMismatch
        }
      />
    </>
  )
}

export default EvmForm
